import { useState } from "react";
import { AlertTriangle, Check, MapPin, X } from "lucide-react";
import { api, ApiError } from "../lib/api";
import type { ColumnSchema } from "../lib/types";

interface GeocodeResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
  kommun: string | null;
  postal_code: string | null;
}

export type Hours = Record<string, { open?: string; close?: string }>;
export type FormValues = Record<string, string | boolean | Hours>;

const DAYS: [string, string][] = [
  ["monday", "Måndag"], ["tuesday", "Tisdag"], ["wednesday", "Onsdag"], ["thursday", "Torsdag"],
  ["friday", "Fredag"], ["saturday", "Lördag"], ["sunday", "Söndag"],
];

const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * The one form. Create and edit differ only in which field list they are handed
 * and where the initial values come from — writing that twice is how the two
 * drift apart.
 *
 * Field type comes from the registry column, not from the current value, so a
 * blank new record still renders a checkbox where the model wants a boolean.
 */
export function buildInitialValues(
  fields: string[],
  columns: ColumnSchema[],
  row?: Record<string, unknown>,
): FormValues {
  const values: FormValues = {};
  for (const field of fields) {
    const column = columnFor(columns, field);
    const current = row?.[field];

    if (column?.type === "hours") {
      values[field] = (current && typeof current === "object" ? current : {}) as Hours;
    } else if (column?.type === "file") {
      values[field] = ""; // a new upload only; the current file shows on the record
    } else if (column?.type === "bool") {
      values[field] = Boolean(current);
    } else {
      values[field] = current === null || current === undefined ? "" : String(current);
    }
  }
  return values;
}

// A write-only upload field ("logo_upload") borrows label and type from the
// read column it feeds ("logo").
const columnFor = (columns: ColumnSchema[], field: string) =>
  columns.find((c) => c.key === field) ?? columns.find((c) => c.key === field.replace(/_upload$/, ""));

export default function RecordForm({
  fields, columns, required, initial, submitLabel, busy, geocoding = false, onSubmit, onCancel,
}: {
  fields: string[];
  columns: ColumnSchema[];
  required: string[];
  initial: FormValues;
  submitLabel: string;
  busy: boolean;
  /** Whether the server has a Google Maps key. Without one the lookup button
   *  is hidden and coordinates are simply typed by hand. */
  geocoding?: boolean;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<FormValues>(initial);
  const [lookup, setLookup] = useState<GeocodeResult | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);

  const set = (field: string, value: string | boolean | Hours) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  // The address is what a human knows; latitude and longitude are what the
  // app's nearby search needs. Rather than ask an operator to find coordinates
  // themselves, look them up and fill both — plus the normalised address and
  // kommun, which is where the typos otherwise come from.
  const canGeocode =
    geocoding && fields.includes("address") && fields.includes("latitude") && fields.includes("longitude");

  const runLookup = async () => {
    const address = String(values.address || "").trim();
    if (!address) {
      setLookupError("Skriv en adress först.");
      return;
    }

    setLooking(true);
    setLookupError(null);
    setLookup(null);
    try {
      const result = await api.get<GeocodeResult>("/superadmin/geocode", { address });
      setValues((prev) => ({
        ...prev,
        latitude: String(result.latitude),
        longitude: String(result.longitude),
      }));
      setLookup(result);
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : "Uppslagningen misslyckades.");
    } finally {
      setLooking(false);
    }
  };

  // Applied on request, not automatically: Google's idea of the address is
  // usually better, but the operator may have typed something deliberately.
  const applyLookup = () => {
    if (!lookup) return;
    setValues((prev) => ({
      ...prev,
      address: lookup.formatted_address,
      ...(lookup.kommun && fields.includes("kommun") ? { kommun: lookup.kommun } : {}),
    }));
    setLookup(null);
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(values); }} className="space-y-3">
      {fields.map((field) => {
        const column = columnFor(columns, field);
        const label = column?.label || field;
        const value = values[field];
        const isRequired = required.includes(field);

        return (
          <div key={field}>
            <label htmlFor={`f-${field}`} className="mb-1 block text-xs font-medium text-ink-400">
              {label}
              {isRequired && <span className="ml-0.5 text-red-400">*</span>}
            </label>

            {column?.type === "hours" ? (
              <div className="space-y-1.5">
                {DAYS.map(([day, name]) => {
                  const hours = (value as Hours)[day] ?? {};
                  const setDay = (part: "open" | "close", v: string) =>
                    set(field, { ...(value as Hours), [day]: { ...hours, [part]: v } });
                  return (
                    <div key={day} className="flex items-center gap-2 text-sm">
                      <span className="w-20 text-ink-400">{name}</span>
                      <input type="time" aria-label={`${name} öppnar`} value={hours.open ?? ""}
                        onChange={(e) => setDay("open", e.target.value)} className="field w-32" />
                      <span className="text-ink-500">–</span>
                      <input type="time" aria-label={`${name} stänger`} value={hours.close ?? ""}
                        onChange={(e) => setDay("close", e.target.value)} className="field w-32" />
                    </div>
                  );
                })}
                <p className="text-xs text-ink-500">Lämna båda tomma för stängt.</p>
              </div>
            ) : column?.type === "file" ? (
              <input
                id={`f-${field}`} type="file" accept="image/png,image/jpeg,image/webp"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  set(field, file ? await readDataUrl(file) : "");
                }}
                className="field"
              />
            ) : typeof value === "boolean" ? (
              <button
                type="button" id={`f-${field}`}
                onClick={() => set(field, !value)}
                className={`btn ${
                  value
                    ? "border border-emerald-900 bg-emerald-950 text-emerald-300"
                    : "border border-ink-700 text-ink-400"
                }`}
              >
                {value ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                {value ? "Ja" : "Nej"}
              </button>
            ) : column?.options ? (
              <select
                id={`f-${field}`} required={isRequired} value={String(value ?? "")}
                onChange={(e) => set(field, e.target.value)}
                className="field"
              >
                {value === "" && <option value="" />}
                {column.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : column?.type === "text" ? (
              <textarea
                id={`f-${field}`} rows={4} required={isRequired} value={String(value ?? "")}
                onChange={(e) => set(field, e.target.value)}
                className="field"
              />
            ) : (
              <div className="flex gap-2">
                <input
                  id={`f-${field}`}
                  required={isRequired}
                  value={String(value ?? "")}
                  onChange={(e) => set(field, e.target.value)}
                  type={column?.type === "email" ? "email" : column?.type === "date" ? "date" : "text"}
                  inputMode={column?.type === "int" || column?.type === "money" ? "numeric" : undefined}
                  placeholder={PLACEHOLDERS[field]}
                  className="field"
                />
                {field === "address" && canGeocode && (
                  <button
                    type="button" onClick={runLookup} disabled={looking}
                    className="btn-ghost shrink-0 whitespace-nowrap"
                    title="Slå upp koordinater för adressen"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    {looking ? "Söker…" : "Hämta koordinater"}
                  </button>
                )}
              </div>
            )}

            {field === "address" && lookupError && (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-300">
                <AlertTriangle className="h-3 w-3 shrink-0" /> {lookupError}
              </p>
            )}

            {field === "address" && lookup && (
              <div className="mt-1.5 rounded-md border border-ink-700 bg-ink-850/60 p-2 text-xs">
                <div className="flex items-start gap-1.5 text-ink-300">
                  <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-accent" />
                  <span>
                    {lookup.formatted_address}
                    <span className="num block text-ink-500">
                      {lookup.latitude}, {lookup.longitude}
                      {lookup.kommun && ` · ${lookup.kommun}`}
                    </span>
                  </span>
                </div>
                <div className="mt-1.5 flex gap-3">
                  <button type="button" onClick={applyLookup} className="text-accent hover:underline">
                    Använd Googles adress{lookup.kommun && " och kommun"}
                  </button>
                  <button
                    type="button" onClick={() => setLookup(null)}
                    className="text-ink-500 hover:text-ink-300"
                  >
                    Behåll min text
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={busy} className="btn-primary">
          {busy ? "Sparar…" : submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">
          Avbryt
        </button>
      </div>
    </form>
  );
}

// Coordinates are the one place a superadmin reliably needs a nudge: a garage
// without them never shows up in the app's nearby search.
const PLACEHOLDERS: Record<string, string> = {
  latitude: "59.3293",
  longitude: "18.0686",
  start_date: "2026-01-01",
  end_date: "2026-12-31",
};
